import { normalizeTags } from "./tagNormalization"
import { beginTagWrite, readTagWrite } from "./tagWriteLedger"
import { enqueueTagWrite } from "./tagWriteQueue"

interface WriteRecordTagsParams {
  recordId: string
  /**
   * The resource the record belongs to, e.g. `"agents"`. Namespaces the write
   * queue and ledger, since ids are only unique within a resource.
   */
  resource: string
  /** The whole tag list to write. */
  tags: readonly string[]
  /** Writes the record's whole tag list. */
  updateTags: (tags: string[]) => Promise<unknown>
}

interface RecordTagWriteOutcome {
  /** What the write failed with, when it did not land. */
  error?: unknown
  landed: boolean
  /**
   * Whether this write is still the record's newest. A later edit built on
   * what this one was setting owns the record instead, and has absorbed this
   * outcome — there is nothing left for the caller to roll back or report.
   */
  ownsRecord: boolean
}

/**
 * What a record is tagged with once its outstanding writes are counted.
 *
 * Rows lag writes: a write only shows up once the list refetches, and one that
 * moves a record out of the active filter drops its row altogether. A tag list
 * built from a row alone can therefore predate an edit that already landed,
 * and writing it back silently drops the tags that edit added.
 */
export function readRecordTags(
  resource: string,
  recordId: string,
  rowTags: readonly string[] | null | undefined,
): string[] {
  return readTagWrite(resource, recordId) ?? normalizeTags(rowTags ?? [])
}

/**
 * Writes a record's whole tag list behind every tag write already queued for
 * the same record, and records it so the other tag controls build their lists
 * from what it is setting.
 *
 * The tag endpoints replace the whole list, so two writes reaching one record
 * at once race and the one the service handles last wins outright — an older
 * list can come back over a newer edit and drop the tags it added. Every
 * control that writes a resource's tags has to go through here for the
 * ordering to hold; `useRecordTagWrite` and `useBulkTagging` are the hook
 * forms, and this is for the callers that write outside a hook.
 */
export async function writeRecordTags({
  recordId,
  resource,
  tags,
  updateTags,
}: WriteRecordTagsParams): Promise<RecordTagWriteOutcome> {
  const normalizedTags = normalizeTags(tags)
  const write = beginTagWrite(resource, recordId, normalizedTags)

  return enqueueTagWrite(resource, recordId, async () => {
    try {
      await updateTags(normalizedTags)
    } catch (error: unknown) {
      return { error, landed: false, ownsRecord: write.settle(false) }
    }

    return { landed: true, ownsRecord: write.settle(true) }
  })
}
